import { ActionTree } from 'vuex';
import { MutationTypes } from '@/store/mutations';
import { ComparingItems, State } from '@/types/vuex/state';
import { Actions } from '@/types/vuex/actions';
import { useLocalStorage } from '@/util/LocalStorage';
import * as api from '@/util/API';
import { swap, diff } from '@/util/ArrayHelper';
import { COMPARING_ITEMS_AMOUNT } from '@/store/index';
import { get as getCookie } from '@/util/Cookie';
import { omitEmptyItem } from '@/util/state/ComparingItems';

export enum ActionTypes {
    InitState = 'INIT_STATE',
    SetIsComparing = 'SET_IS_COMPARING',
    InitComparisonView = 'INIT_COMPARISON_VIEW',
    GetComparisonData = 'GET_COMPARISON_DATA',
    GetItemsShortInfo = 'GET_ITEMS_SHORT_INFO',
    ItemsInfoFetched = 'GET_ITEMS_FETCHED',
    SetIsDisplayingDifference = 'SET_IS_DISPLAYING_DIFFERENCE',
    FetchListViewData = 'FETCH_LIST_VIEW_DATA',
    InitListView = 'INIT_LIST_VIEW',
    ShowMoreListItems = 'SHOW_MORE_LIST_ITEMS',
    InitComparingItems = 'INIT_COMPARING_ITEMS',
    ItemSelected = 'ITEM_SELECTED',
    FetchMissingComparingItems = 'FETCH_MISSING_COMPARING_ITEMS',
    NormalizeComparingItems = 'NORMALIZE_COMPARING_ITEMS',
    RemoveItemFromCompareList = 'REMOVE_ITEM_FROM_COMPARE_LIST',
    RemoveItemFromCompareCompletely = 'REMOVE_ITEM_FROM_COMPARE_COMPLETELY',
    SetIsMenuTechSpecOpened = 'SET_IS_MENU_TECH_SPEC_OPENED',
    SetIsMenuSellerOpened = 'SET_IS_MENU_SELLER_OPENED',
    TickItemToCompare = 'TICK_ITEM_TO_COMPARE',
    UntickItem = 'UNTICK_TICK_ITEM',
    DeleteItem = 'DELETE_ITEM',
    SetIsEmptyTechSpec = 'SET_IS_EMPTY_TECH_SPEC',
    DefineIsEmptyTechSpec = 'DEFINE_IS_EMPTY_TECH_SPEC',
    TryToOmitOneComparingItem = 'TRY_TO_OMIT_ONE_COMPARING_ITEM',
    OpenIncludedOptionsModal = 'OPEN_INCLUDED_OPTIONS_MODAL',
}

type BackendState = {
    isComparing?: boolean|null;
    isDisplayingDiff?: boolean|null;
    isTechOpened?: boolean|null;
    isSellerOpened?: boolean|null;
    comparingItems?: number[]|null;
    items?: number[]|null;
}

export const actions: ActionTree<State, State> & Actions = {
    [ActionTypes.InitState] ({ commit, state }, json) {
        const backendState = (json ? JSON.parse(json) : {}) as BackendState;
        const localStorage = useLocalStorage();
        const lsState = localStorage.getState();
        let shortlistItems;
        if (Array.isArray(backendState.items)) {
            shortlistItems = backendState.items;
        } else {
            shortlistItems = Array.isArray(lsState.items)
                ? lsState.items
                : getCookie(state.auxData.cookieKey);
        }
        commit(MutationTypes.SetItems, shortlistItems);
        if (typeof backendState.isComparing === 'undefined') {
            commit(MutationTypes.SetIsComparing, lsState.isComparing === undefined
                ? true
                : !!lsState.isComparing);
        } else {
            commit(MutationTypes.SetIsComparing, backendState.isComparing);
            localStorage.setBoolean('isComparing', backendState.isComparing);
        }
        if (typeof backendState.isDisplayingDiff === 'undefined') {
            commit(MutationTypes.SetIsDisplayingDifference, !(lsState.showAll ?? true));
        } else {
            commit(MutationTypes.SetIsDisplayingDifference, backendState.isDisplayingDiff);
            localStorage.setBoolean('showAll', !backendState.isDisplayingDiff);
        }
        if (typeof backendState.isSellerOpened === 'undefined') {
            commit(MutationTypes.SetMenuSellerOpened, !lsState.sellerCollapsed);
        } else {
            commit(MutationTypes.SetMenuSellerOpened, backendState.isSellerOpened);
            localStorage.setBoolean('sellerCollapsed', !backendState.isSellerOpened);
        }
        if (typeof backendState.isTechOpened === 'undefined') {
            commit(MutationTypes.SetMenuTechSpecOpened, !!(lsState.techOpened ?? true));
        } else {
            commit(MutationTypes.SetMenuTechSpecOpened, backendState.isTechOpened);
            localStorage.setBoolean('techOpened', backendState.isTechOpened);
        }
        if (Array.isArray(backendState.comparingItems)) {
            commit(MutationTypes.SetComparingItemsFromBackend, backendState.comparingItems);
            localStorage.set('comparingItems', backendState.comparingItems);
        }
    },
    async [ActionTypes.SetIsComparing] ({ state, commit }, isComparing: boolean) {
        useLocalStorage().setBoolean('isComparing', isComparing);
        commit(MutationTypes.SetIsComparing, isComparing);
        await api.setIsComparing(state.apiUrl, isComparing);
    },
    async [ActionTypes.FetchMissingComparingItems] ({ getters, dispatch, state }) {
        const itemsToRequest = getters.notEmptyComparingItems.filter(id => !state.comparisonCache[id]);
        if (itemsToRequest.length > 0) {
            await dispatch(ActionTypes.InitComparisonView, itemsToRequest);
        } else {
            await dispatch(ActionTypes.NormalizeComparingItems);
        }
    },
    [ActionTypes.NormalizeComparingItems] ({ commit, state }) {
        for (let i = 0; i < COMPARING_ITEMS_AMOUNT; i++) {
            commit(MutationTypes.NormalizeItemState, { index: i, clear: !state.tickedItems[i] });
        }
    },
    async [ActionTypes.InitComparisonView] ({ commit, dispatch }, items) {
        try {
            commit(MutationTypes.ComparisonIsLoading);
            await dispatch(ActionTypes.GetComparisonData, items);
            await dispatch(ActionTypes.NormalizeComparingItems);
        } catch (e) {
            console.error(e);
            commit(MutationTypes.ApiErrorOccurred);
        }
    },
    async [ActionTypes.GetComparisonData] (context, items) {
        const data = await api.getVehiclesInfo(context.state.apiUrl, items);
        const vehiclesInfo = {};

        items.forEach(id => {
            if (data[id]) {
                vehiclesInfo[id] = data[id];
            }
        });

        context.commit(MutationTypes.ItemsInfoFetched, vehiclesInfo);
    },
    async [ActionTypes.GetItemsShortInfo] ({ state, commit, dispatch }) {
        try {
            commit(MutationTypes.ComparisonIsLoading);
            const data = await api.getVehiclesShortInfo(state.apiUrl, state.items);
            const shortlistItems = [];
            const vehicles = [];

            data.forEach(vehicle => {
                const id = +vehicle.id;
                if (id) {
                    shortlistItems.push(id);
                    vehicles.push(vehicle);
                }
            });

            useLocalStorage().set('items', shortlistItems);
            commit(MutationTypes.SetItems, shortlistItems);

            await dispatch(ActionTypes.InitComparingItems);
            commit(MutationTypes.ItemsShortInfoFetched, vehicles);
        } catch (e) {
            console.error(e);
            commit(MutationTypes.ApiErrorOccurred);
        }
    },
    async [ActionTypes.SetIsDisplayingDifference] ({ commit, state }, isDisplaying) {
        useLocalStorage().setBoolean('showAll', !isDisplaying);
        commit(MutationTypes.SetIsDisplayingDifference, isDisplaying);
        if (state.auxData.isAuthenticated) {
            await api.setIsDisplayingDifference(state.apiUrl, isDisplaying);
        }
    },
    async [ActionTypes.InitListView] ({ commit, dispatch }) {
        try {
            commit(MutationTypes.ListIsLoading);
            await dispatch(ActionTypes.FetchListViewData);
            commit(MutationTypes.ListFinishedLoading);
        } catch (e) {
            console.error(e);
            commit(MutationTypes.ApiErrorOccurred);
        }
    },
    async [ActionTypes.ShowMoreListItems] ({ commit, dispatch }) {
        try {
            commit(MutationTypes.NewListPageIsLoading);
            await dispatch(ActionTypes.FetchListViewData);
            commit(MutationTypes.NewListPageLoaded);
        } catch (e) {
            console.error(e);
            commit(MutationTypes.ApiErrorOccurred);
        }
    },
    async [ActionTypes.FetchListViewData] ({ state, commit, dispatch, getters }) {
        const data = await api.getVehiclesHtml(state.apiUrl, state.items, state.itemsPerPage, getters.listItems.length);
        const deadVehicles = diff(state.items, data.liveIds);
        deadVehicles.forEach(id => {
            dispatch(ActionTypes.DeleteItem, id);
        });
        useLocalStorage().set('items', data.liveIds);
        commit(MutationTypes.SetItems, data.liveIds);
        await dispatch(ActionTypes.InitComparingItems);
        commit(MutationTypes.ListDataFetched, data.items);
    },
    [ActionTypes.InitComparingItems] ({ commit, state, dispatch }) {
        if (state.comparingItemInitialized) {
            return;
        }
        const localStorage = useLocalStorage();
        let tickedItems;
        if (Array.isArray(state.comparingItemsFromBackend)) {
            tickedItems = [...state.comparingItemsFromBackend];
            commit(MutationTypes.ClearComparingItemsFromBackend);
        } else {
            tickedItems = localStorage.getComparingItems();
        }
        let comparingItems;
        if (Array.isArray(tickedItems)) {
            comparingItems = tickedItems.filter(item => item === 0 || state.items.includes(item));
        } else {
            comparingItems = [state.items[0] || 0, state.items[1] || 0, 0];
        }
        if (comparingItems.length > COMPARING_ITEMS_AMOUNT) {
            comparingItems.splice(COMPARING_ITEMS_AMOUNT);
        }
        if (comparingItems.length < COMPARING_ITEMS_AMOUNT) {
            const emptyArray = new Array(COMPARING_ITEMS_AMOUNT - comparingItems.length).fill(0);
            comparingItems = [...comparingItems, ...emptyArray];
        }
        dispatch(ActionTypes.TryToOmitOneComparingItem, comparingItems);
        commit(MutationTypes.ComparingItemsInitialized);
    },
    async [ActionTypes.ItemSelected] ({ state, commit, dispatch }, payload) {
        if (!state.comparisonCache[payload.id]) {
            try {
                commit(MutationTypes.FetchingVehicleComparisonData, payload.index);
                await dispatch(ActionTypes.GetComparisonData, [payload.id]);
                commit(MutationTypes.VehicleComparisonDataFetched, payload.index);
            } catch (e) {
                console.error(e);
                commit(MutationTypes.ApiErrorOccurred);
                return;
            }
        }
        const index = state.tickedItems.indexOf(payload.id);
        if (payload.id && index !== -1) {
            const tickedItems = swap(state.tickedItems, payload.index, index);
            useLocalStorage().set('comparingItems', tickedItems);
            commit(MutationTypes.SetTickedItems, tickedItems as [number, number, number]);
            commit(MutationTypes.SwapItems, { index1: payload.index, index2: index });
            if (state.auxData.isAuthenticated) {
                await api.setComparingItems(state.apiUrl, tickedItems);
            }
        } else {
            const tickedItems = [...state.tickedItems];
            tickedItems[payload.index] = payload.id;
            useLocalStorage().set('comparingItems', tickedItems);
            commit(MutationTypes.SetTickedItems, tickedItems as [number, number, number]);
            commit(MutationTypes.NormalizeItemState, { index: payload.index });
            if (state.auxData.isAuthenticated) {
                await api.setComparingItems(state.apiUrl, tickedItems);
            }
        }
    },
    async [ActionTypes.RemoveItemFromCompareList] (context, index) {
        await context.dispatch(ActionTypes.UntickItem, context.state.tickedItems[index]);
        context.commit(MutationTypes.NormalizeItemState, { index: index, clear: true });
    },
    async [ActionTypes.RemoveItemFromCompareCompletely] ({ state, dispatch }, index) {
        const id = state.tickedItems[index];
        await dispatch(ActionTypes.DeleteItem, id);
    },
    async [ActionTypes.SetIsMenuTechSpecOpened] ({ commit, state }, isOpened) {
        useLocalStorage().setBoolean('techOpened', isOpened);
        commit(MutationTypes.SetMenuTechSpecOpened, isOpened);
        if (state.auxData.isAuthenticated) {
            await api.setIsTechOpened(state.apiUrl, isOpened);
        }
    },
    async [ActionTypes.SetIsMenuSellerOpened] ({ commit, state }, isOpened) {
        useLocalStorage().setBoolean('sellerCollapsed', !isOpened);
        commit(MutationTypes.SetMenuSellerOpened, isOpened);
        if (state.auxData.isAuthenticated) {
            await api.setIsSellerOpened(state.apiUrl, isOpened);
        }
    },
    async [ActionTypes.TickItemToCompare] ({ getters, state, commit }, itemId: number) {
        itemId = +itemId;
        const items = getters.notEmptyComparingItems;
        if (state.tickedItems.includes(itemId) || items.length >= COMPARING_ITEMS_AMOUNT) {
            return;
        }
        const ticked = [itemId, ...items, ...[0, 0, 0]] as ComparingItems;
        ticked.splice(getters.isLastColumnHidden
            ? COMPARING_ITEMS_AMOUNT - 1
            : COMPARING_ITEMS_AMOUNT);
        useLocalStorage().set('comparingItems', ticked);
        commit(MutationTypes.SetTickedItems, ticked);
        if (state.auxData.isAuthenticated) {
            await api.setComparingItems(state.apiUrl, ticked);
        }
    },
    async [ActionTypes.UntickItem] ({ state, dispatch }, itemId: number) {
        itemId = +itemId;
        if (!state.tickedItems.includes(itemId)) {
            return;
        }
        const ticked = [...state.tickedItems];
        const index = ticked.indexOf(itemId);
        if (index === -1) {
            return;
        }
        ticked[index] = 0;
        await dispatch(ActionTypes.TryToOmitOneComparingItem, ticked);

        if (ticked.some(id => id !== 0)) {
            useLocalStorage().set('comparingItems', ticked);
        } else {
            useLocalStorage().unset('comparingItems');
        }

        if (state.auxData.isAuthenticated) {
            await api.setComparingItems(state.apiUrl, ticked);
        }
    },
    async [ActionTypes.DeleteItem] ({ dispatch, state, commit }, itemId: number) {
        itemId = +itemId;
        const items = [...state.items];
        const index = items.indexOf(itemId);
        if (index > -1) {
            items.splice(index, 1);
        }
        const localStorage = useLocalStorage();
        localStorage.set('items', items);
        commit(MutationTypes.SetItems, items);
        await dispatch(ActionTypes.UntickItem, itemId);
        if (!state.isComparing) {
            commit(MutationTypes.SetCheckIfNeedToLoadMoreItems, true);
        }
        if (state.auxData.isAuthenticated) {
            await api.removeFromShortlist(state.apiUrl, itemId);
        }
        commit(MutationTypes.DeleteItemData, itemId);
    },
    [ActionTypes.SetIsEmptyTechSpec] ({ commit }, isEmpty: boolean) {
        commit(MutationTypes.SetIsEmptyTechSpec, isEmpty);
    },
    [ActionTypes.DefineIsEmptyTechSpec] ({ state, getters, dispatch }) {
        const hasVan = state.isVan.some(item => Boolean(item));

        const isEmpty = getters.isEmptyValues([
            getters.valuesOf(state.enginePower),
            getters.valuesOf(state.acceleration),
            getters.valuesOf(state.topSpeed),
            getters.valuesOf(state.enginePower),
            getters.valuesOf(state.acceleration),
            getters.valuesOf(state.topSpeed),
            getters.valuesOf(state.fuelConsumption),
            getters.valuesOf(state.fuelTankCapacity),
            getters.valuesOf(state.combinedRange),
            getters.valuesOf(state.co2Emissions),
            getters.valuesOf(state.numberOfSeats),
            getters.valuesOf(state.length),
            getters.valuesOf(state.width),
            getters.valuesOf(state.height),
            getters.valuesOf(state.weight),
            getters.valuesOf(state.luggage),
            getters.valuesOf(state.wheelType),
            hasVan && getters.valuesOf(state.loadHeight),
            hasVan && getters.valuesOf(state.loadLength),
            hasVan && getters.valuesOf(state.payload),
            hasVan && getters.valuesOf(state.towingWeight)
        ].filter(item => Boolean(item)));

        dispatch(ActionTypes.SetIsEmptyTechSpec, isEmpty);
    },
    [ActionTypes.TryToOmitOneComparingItem] ({ commit, state, dispatch }, comparingItems) {
        try {
            commit(MutationTypes.SetTickedItems, omitEmptyItem(state.items, comparingItems));
            dispatch(ActionTypes.NormalizeComparingItems);
        } catch (e) {
            commit(MutationTypes.SetTickedItems, comparingItems);
        }
    },
    async [ActionTypes.OpenIncludedOptionsModal] ({ commit, state }, index) {
        commit(MutationTypes.OpenIncludedOptionsModal, index);
        const id = state.tickedItems[index];
        if (state.includedOptionsCache[id]) {
            return;
        }
        try {
            const includedOptions = await api.getIncludedOptions(state.apiUrl, id);
            commit(MutationTypes.IncludedOptionsFetched, {
                id: id,
                options: includedOptions
            });
        } catch (e) {
            commit(MutationTypes.ApiErrorOccurred);
        }
    }
};
